import { useMutation, useQuery } from "@apollo/client";
import {
  Badge,
  Button,
  Card,
  Drawer,
  Group,
  LoadingOverlay,
  Modal,
  ScrollArea,
  Select,
  Switch,
  useMantineTheme,
} from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import EditIcon from "@mui/icons-material/Edit";
import {
  DELETE_LOAN_PROVIDER_STAFF,
  TOGGLE_LOAN_PROVIDER_STAFF_STATUS,
} from "apollo/mutuations";
import { LOAN_PROVIDER_STAFF } from "apollo/queries";
import ActionButton from "components/controls/ActionButtonNew";
import StaffAddModal from "components/LoanProviderStaff/StaffAddModal";
import StaffEditModal from "components/LoanProviderStaff/StaffEditModal";
import B2bTable from "components/reusable/b2bTable";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";
import { Trash } from "tabler-icons-react";

const LoanProviderStaff = () => {
  const [size, setSize] = useState("10");
  const [opened, setOpened] = useState(false);
  const [openedDelete, setOpenedDelete] = useState(false);
  const [openedEdit, setOpenedEdit] = useState(false);
  const [editId, setEditId] = useState();
  const [deleteID, setDeleteID] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [total, setTotal] = useState(0);
  const [roleFilter, setRoleFilter] = useState(null);

  const { data, loading, refetch } = useQuery(LOAN_PROVIDER_STAFF, {
    variables: {
      first: parseInt(size),
      page: activePage,
      search: searchValue,
      role: roleFilter,
      orderBy: [
        {
          column: "created_at",
          order: "DESC",
        },
      ],
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to Get Staff Members",
      });
    },
  });

  const handlePageSizeChange = (newSize) => {
    setSize(newSize);
    setActivePage(1);
  };

  useEffect(() => {
    if (data) {
      setTotal(data.loanProviderStaff.paginatorInfo.lastPage);
    }
  }, [data, size]);

  useEffect(() => {
    refetch();
  }, []);

  const [delStaff] = useMutation(DELETE_LOAN_PROVIDER_STAFF, {
    update(cache, { data: { deleteLoanProviderStaff } }) {
      cache.updateQuery(
        {
          query: LOAN_PROVIDER_STAFF,
          variables: {
            first: parseInt(size),
            page: activePage,
            search: searchValue,
            role: roleFilter,
            orderBy: [
              {
                column: "created_at",
                order: "DESC",
              },
            ],
          },
        },
        (data) => {
          if (data.loanProviderStaff.data.length === 1) {
            setTotal(total - 1);
            setActivePage(activePage - 1);
          } else {
            return {
              loanProviderStaff: {
                data: [
                  ...data.loanProviderStaff.data.filter(
                    (staff) => staff.id !== deleteLoanProviderStaff.id,
                  ),
                ],
              },
            };
          }
        },
      );
    },
  });

  const [toggleStaffStatus] = useMutation(TOGGLE_LOAN_PROVIDER_STAFF_STATUS, {
    onCompleted(data) {
      showNotification({
        color: "green",
        title: "Success",
        message: "Staff Status Updated Successfully",
      });
      refetch();
    },
    onError(error) {
      showNotification({
        color: "red",
        title: "Error",
        message:
          error?.graphQLErrors?.[0]?.message || "Failed to Update Status",
      });
    },
  });

  const theme = useMantineTheme();

  const handleToggleStatus = (staffId) => {
    toggleStaffStatus({
      variables: { id: staffId },
    });
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "MAKER":
        return "blue";
      case "CHECKER":
        return "cyan";
      case "APPROVER":
        return "green";
      default:
        return "gray";
    }
  };

  const headerData = [
    {
      label: "#",
      key: "index",
      sortable: false,
      searchable: false,
      width: "5%",
      render: (_rowData, rowIndex) => {
        const idx = (activePage - 1) * parseInt(size || "0") + (rowIndex + 1);
        return <span>{idx}</span>;
      },
    },
    {
      label: "Name",
      key: "name",
      width: "20%",
      sortable: true,
      searchable: true,
      render: (rowData) => {
        return <span>{rowData.user?.name || "N/A"}</span>;
      },
    },
    {
      label: "Email",
      key: "email",
      sortable: false,
      searchable: true,
      width: "25%",
      render: (rowData) => {
        return <span>{rowData.user?.email || "N/A"}</span>;
      },
    },
    {
      label: "Phone",
      key: "phone",
      sortable: false,
      searchable: true,
      width: "15%",
      render: (rowData) => {
        return <span>{rowData.user?.phone || "N/A"}</span>;
      },
    },
    {
      label: "Role",
      key: "role",
      sortable: true,
      searchable: false,
      width: "12%",
      render: (rowData) => {
        return (
          <Badge color={getRoleBadgeColor(rowData.role)} variant="filled">
            {rowData.role}
          </Badge>
        );
      },
    },
    {
      label: "Status",
      key: "is_active",
      sortable: false,
      searchable: false,
      width: "10%",
      render: (rowData) => {
        return (
          <Switch
            checked={rowData.is_active}
            onChange={() => handleToggleStatus(rowData.id)}
            color="green"
            size="md"
          />
        );
      },
    },
    {
      label: "Actions",
      key: "actions",
      sortable: false,
      searchable: false,
      width: "13%",
      render: (rowData) => {
        return (
          <div style={{ display: "flex", gap: "6px" }}>
            <ActionButton label="Edit" onClick={() => handleEditStaff(rowData)}>
              <EditIcon style={{ fontSize: "1rem" }} />
            </ActionButton>
            <ActionButton
              label="Delete"
              color="error"
              onClick={() => handleDelete(`${rowData.id}`)}
            >
              <Trash size={15} />
            </ActionButton>
          </div>
        );
      },
    },
  ];

  const optionsData = {
    actionLabel: "Add Staff Member",
    setAddModal: setOpened,
  };

  const handleDelete = (id) => {
    setOpenedDelete(true);
    setDeleteID(id);
  };

  const handleEditStaff = (staff) => {
    setOpenedEdit(true);
    setEditId(staff);
  };

  const handleChange = (currentPage) => {
    setActivePage(currentPage);
  };

  const deleteStaff = () => {
    delStaff({
      variables: { id: deleteID },
      onCompleted(data) {
        setOpenedDelete(false);
        setDeleteID(null);
        showNotification({
          color: "green",
          title: "Success",
          message: "Staff Member Deleted Successfully",
        });
      },
      onError(error) {
        setOpenedDelete(false);
        setDeleteID(null);
        showNotification({
          color: "red",
          title: "Error",
          message:
            error?.graphQLErrors?.[0]?.message ||
            "Failed to Delete Staff Member",
        });
      },
    });
  };

  const [confirmedSearch, setConfirmedSearch] = useState("");

  const handleManualSearch = (searchTerm) => {
    setSearchValue(searchTerm);
  };

  const clearInput = () => {
    setSearchValue("");
    setConfirmedSearch("");
  };

  return loading ? (
    <LoadingOverlay
      visible={loading}
      color="blue"
      overlayBlur={2}
      loader={customLoader}
    />
  ) : (
    <div style={{ width: "98%", margin: "auto" }}>
      <Drawer
        opened={opened}
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
        padding="xl"
        onClose={() => setOpened(false)}
        position="left"
        size="40%"
        styles={{
          closeButton: {
            color: "black",
            marginTop: "50px",
          },
        }}
      >
        <StaffAddModal
          total={total}
          setTotal={setTotal}
          activePage={activePage}
          setActivePage={setActivePage}
          setOpened={setOpened}
          size={size}
          refetch={refetch}
        />
      </Drawer>

      <Drawer
        opened={openedEdit}
        overlayColor={
          theme.colorScheme === "dark"
            ? theme.colors.dark[9]
            : theme.colors.gray[2]
        }
        overlayOpacity={0.55}
        overlayBlur={3}
        styles={{
          closeButton: {
            color: "black",
            marginTop: "50px",
          },
        }}
        padding="xl"
        onClose={() => setOpenedEdit(false)}
        position="right"
        size="40%"
      >
        <StaffEditModal
          setOpenedEdit={setOpenedEdit}
          editData={editId}
          data={data ? data.loanProviderStaff.data : []}
        />
      </Drawer>

      <Modal
        opened={openedDelete}
        onClose={() => setOpenedDelete(false)}
        title="Warning"
        centered
      >
        <p>Are you sure you want to delete this staff member?</p>
        <Group position="right">
          <Button onClick={() => deleteStaff()} color="red">
            Delete
          </Button>
        </Group>
      </Modal>

      {/* Filters Section */}
      <Card shadow="sm" p="md" mb="md">
        <Group spacing="md" align="flex-end">
          <Select
            label="Role"
            placeholder="All Roles"
            clearable
            value={roleFilter}
            onChange={(value) => {
              setRoleFilter(value);
              setActivePage(1);
            }}
            data={[
              { value: "MAKER", label: "Maker" },
              { value: "CHECKER", label: "Checker" },
              { value: "APPROVER", label: "Approver" },
            ]}
            style={{ minWidth: 200 }}
            withinPortal
          />
        </Group>
      </Card>

      <Card shadow="sm" p="lg">
        <ScrollArea>
          <B2bTable
            total={total}
            activePage={activePage}
            handleChange={handleChange}
            header={headerData}
            clearInput={clearInput}
            paginatorInfo={data?.loanProviderStaff.paginatorInfo}
            handelSearch={handleManualSearch}
            searchValue={confirmedSearch}
            onSearchChange={setConfirmedSearch}
            optionsData={optionsData}
            loading={loading}
            data={data ? data.loanProviderStaff.data : []}
            size={size}
            handlePageSizeChange={handlePageSizeChange}
          />
        </ScrollArea>
      </Card>
    </div>
  );
};

export default LoanProviderStaff;
